import {
  faAt,
  faBriefcase,
  faCircleInfo,
  faGlobe,
  faMagicWandSparkles,
  faPerson,
  faPhone,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "sonner";
import { homeState } from "../../../../../state/home";
import { useUploadLeadMatrix, useGetLeadMatrix, useDeleteLeadMatrix } from "../../../../../services/organtization/lead/matrix";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import {
  type PropsWithChildren,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { trim } from "validator";
import type { Lead } from "../../../../../client";
import {
  Button,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  isEmailOrEmpty,
  isPhoneNumberOrEmpty,
  ImageButton,
  isUrlOrEmpty,
  SecondarySectionTitle,
  Image,
  TextButton,
  InputRef,
  isString,
  isUrlWithMaxLengthOrEmpty,
} from "../../../../../components";
import {
  getIconByName,
  iconForName,
} from "../../../../../components/TechIcons";
import { useAnimate, useStatsigClient } from "../../../../../hooks";
import { LeadRowCard } from "../card/LeadRowCard";
import { LeadRowCardSection } from "../card/LeadRowCardSection";
import { LeadRowCardSectionWithLink } from "../card/LeadRowCardSectionWithLink";
import { PanelContainer } from "./PanelContainer";
import { cn } from "../../../../../components/utils/cn";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFaceFrown } from "@fortawesome/free-regular-svg-icons";
import { useGetMatch } from "../../../../../services/organtization/get-match";
import { useGetOrganization } from "../../../../../services";
import { getImageUrl } from "../../../../../utils/image";
import { getDateSectionConfigFromFormatToken } from "@mui/x-date-pickers/internals/hooks/useField/useField.utils";
import { useGetLeadMatch } from "../../../../../services/organtization/get-lead-match";
import SkillsIcon from "../../../../../components/atoms/SkillsIcon";
import EditorSkillTagButton from "../../../../../components/atoms/EditorSkillTagButton";
import { getLeadSkillNames } from "../../../../../utils/get-lead-skills";

interface Props {
  lead?: Lead;
  disabled: boolean;
  onMutateLead: (lead: Partial<Lead>) => void;
  onRequestAssignmentInfo: (url: string) => void;
  onRequestAssignmentInfoWithInfo: (info: string) => void;
  onRequestWebsiteInfo: (url: string) => void;
  onSelectLogo: (logoUrl: string) => void;
  isEditingAssignmentLink: boolean;
  isEditingWebsiteLink: boolean;
  onEditAssigmentLinkClick: () => void;
  onEditWebsiteLinkClick: () => void;
  couldNotLoadFromUrl: boolean;
  couldNotLoadClientFromUrl: boolean;
  organizationId: string;
  onMatchingMembers: (numberOfMatchingMembers: number) => void;
  className?: string;
  stacked?: boolean;
}

const MAX_VISIBLE_SKILLS = 5;

const formatRemote = (value: string) => {
  switch (value) {
    case "remote":
      return "Remote";
    case "remote_possible":
      return "Remote possible";
    case "no_remote":
      return "No remote";
    default:
      return value;
  }
};

export const AssignmentPanel = ({
  lead,
  disabled,
  onMutateLead,
  onRequestAssignmentInfo,
  onRequestAssignmentInfoWithInfo,
  onRequestWebsiteInfo,
  onSelectLogo,
  isEditingAssignmentLink,
  isEditingWebsiteLink,
  onEditAssigmentLinkClick,
  onEditWebsiteLinkClick,
  couldNotLoadFromUrl,
  couldNotLoadClientFromUrl,
  organizationId,
  onMatchingMembers,
  className,
  stacked,
}: Props) => {
  const [skillsParent] = useAnimate();
  const [expanded, toggleExpanded] = useReducer((expanded) => !expanded, false);

  const statsigClient = useStatsigClient();
  const showMatrix = statsigClient?.checkGate("competence_matrix") ?? false;

  const { data, error } = useGetOrganization(organizationId);

  const { data: skillMatchData, error: skillMatchError } = useGetLeadMatch(
    {
      organizationId: lead?.orgId!,
      leadId: lead?.id ?? "",
      skills: getLeadSkillNames(lead),
    },
    { enabled: getLeadSkillNames(lead).length > 0 },
  );

  useEffect(() => {
    if (skillMatchData) {
      onMatchingMembers(skillMatchData.length);
    }
  }, [skillMatchData, onMatchingMembers]);

  const companyUrlInputRef = useRef<InputRef>(null);
  const matrixFileInputRef = useRef<HTMLInputElement>(null);

  const uploadMatrix = useUploadLeadMatrix(organizationId, lead?.id ?? "");
  const deleteMatrix = useDeleteLeadMatrix(organizationId, lead?.id ?? "");
  const { data: matrixData } = useGetLeadMatrix(organizationId, lead?.id ?? "");
  const hasMatrixTemplate = !!matrixData;

  const handleMatrixUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMatrix.mutate(file, {
      onSuccess: () => toast.success("Competence matrix uploaded"),
      onError: () => toast.error("Failed to upload competence matrix"),
    });
    e.target.value = "";
  };

  const handleCompanyUrlSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (companyUrlInputRef.current?.value) {
        onRequestWebsiteInfo(companyUrlInputRef.current.value);
      }
    },
    [onRequestWebsiteInfo],
  );

  const [localContactName, setLocalContactName] = useState(
    lead?.contactPersonName ?? "",
  );
  const [localContactEmail, setLocalContactEmail] = useState(
    lead?.contactPersonEmail ?? "",
  );
  const [localContactPhone, setLocalContactPhone] = useState(
    lead?.contactPersonPhone ?? "",
  );
  const [localCompanyName, setLocalCompanyName] = useState(
    lead?.companyName ?? "",
  );

  const [localNotes, setLocalNotes] = useState(lead?.notes ?? "");

  const [localAssignmentTitle, setLocalAssignmentTitle] = useState(
    lead?.assignmentInfo?.title ?? "",
  );

  useEffect(() => {
    // Update local state when lead data changes
    setLocalContactName(lead?.contactPersonName ?? "");
    setLocalContactEmail(lead?.contactPersonEmail ?? "");
    setLocalContactPhone(lead?.contactPersonPhone ?? "");
    setLocalCompanyName(lead?.companyName ?? "");
    setLocalNotes(lead?.notes ?? "");
    setLocalAssignmentTitle(
      lead?.assignmentInfo?.title ?? lead?.parsedAssignmentData?.title ?? "",
    );
  }, [
    lead?.contactPersonName,
    lead?.contactPersonEmail,
    lead?.contactPersonPhone,
    lead?.companyName,
    lead?.notes,
    lead?.assignmentInfo,
  ]);

  const getProfilePhotoIds = (profileId: string) => {
    return data.profiles.filter((p) => p.profileId === profileId)[0]
      .profilePhotoId;
  };

  const requiredSkills = () => {
    if (
      lead?.parsedAssignmentData &&
      lead.parsedAssignmentData.skills &&
      lead.parsedAssignmentData.skills.technical_required
    ) {
      return lead.parsedAssignmentData.skills.technical_required.map(
        (skill) => skill,
      );
    }
    return [];
  };

  if (lead?.parsedAssignmentData) {
    return (
      <PanelContainer className={cn(stacked && "grid-cols-1", className)}>
        <div className={cn("flex flex-col gap-6 lg:justify-start", stacked && "row-start-2 min-w-0")}>
          <LeadRowCard type="solid">
            <LeadRowCardSection title="Contact information">
              <Input
                leftIcon={faPerson}
                label="Contact name"
                value={localContactName}
                onChange={(e) => setLocalContactName(e.target.value)}
                onBlur={(e) => {
                  if (!(!lead && localContactName === "")) {
                    onMutateLead({ contactPersonName: localContactName });
                  }
                }}
                transformValue={trim}
              />
              <Input
                leftIcon={faAt}
                label="E-mail"
                value={lead?.contactPersonEmail}
                onChange={(e) => setLocalContactEmail(e.target.value)}
                onBlur={(e) => {
                  if (!(!lead && localContactEmail === "")) {
                    onMutateLead({ contactPersonEmail: localContactEmail });
                  }
                }}
                transformValue={trim}
                validate={isEmailOrEmpty}
                type="email"
              />
              <Input
                leftIcon={faPhone}
                label="Phone number"
                value={lead?.contactPersonPhone}
                onChange={(e) => setLocalContactPhone(e.target.value)}
                onBlur={(e) => {
                  if (!(!lead && localContactPhone === "")) {
                    onMutateLead({ contactPersonPhone: localContactPhone });
                  }
                }}
                transformValue={trim}
                validate={isPhoneNumberOrEmpty}
                type="tel"
              />
            </LeadRowCardSection>
          </LeadRowCard>
          <LeadRowCard type="solid">
            {lead?.companyUrl && !isEditingWebsiteLink ? (
              <>
                <LeadRowCardSectionWithLink
                  title="Company"
                  linkUrl={lead.companyUrl}
                  onEditClick={() => onEditWebsiteLinkClick()}
                ></LeadRowCardSectionWithLink>
                <LeadRowCardSection title="Name" secondarySection>
                  <Input
                    disabled={disabled}
                    leftIcon={faGlobe}
                    label="Company website link"
                    onChange={(e) => setLocalCompanyName(e.target.value)}
                    value={localCompanyName}
                    onBlur={(e) => {
                      if (!(!lead && localCompanyName === "")) {
                        onMutateLead({ companyName: localCompanyName });
                      }
                    }}
                  />
                </LeadRowCardSection>
                <LeadRowCardSection title="Pick image">
                  <div className="flex flex-row flex-wrap gap-2">
                    {lead?.logoSuggestions &&
                      lead?.logoSuggestions.map((logo, i) => (
                        <ImageButton
                          key={logo}
                          active={lead.logoSuggestionOrigin === logo}
                          alt={`Logo suggestion ${i}`}
                          imageUrl={logo}
                          onClick={() => onSelectLogo(logo)}
                        />
                      ))}
                    <TextButton
                      key={"custom-icon"}
                      active={
                        !lead.logoSuggestionOrigin ||
                        lead.logoSuggestionOrigin === ""
                      }
                      onClick={() => {
                        onSelectLogo("");
                      }}
                      text={lead.companyName ? lead.companyName[0] : ""}
                    />
                  </div>
                </LeadRowCardSection>
                <LeadRowCardSection
                  title="Description"
                  secondarySection
                  className="text-sm"
                >
                  {lead.companyDesc}
                </LeadRowCardSection>
              </>
            ) : (
              <>
                <LeadRowCardSection title="Company">
                  <form onSubmit={handleCompanyUrlSubmit}>
                    <Input
                      ref={companyUrlInputRef}
                      disabled={disabled}
                      leftIcon={faGlobe}
                      label="Company website link"
                      validate={isUrlWithMaxLengthOrEmpty}
                      value={lead?.companyUrl}
                      rightButton={{
                        label: "Go!",
                        onClick: () => {
                          if (
                            companyUrlInputRef.current?.value &&
                            companyUrlInputRef.current?.value.length < 200
                          )
                            onRequestWebsiteInfo(
                              companyUrlInputRef.current?.value || "",
                            );
                        },
                      }}
                      // Add onKeyDown handler if you specifically want to handle Enter key
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          onRequestWebsiteInfo(
                            companyUrlInputRef.current?.value || "",
                          );
                        }
                      }}
                    />
                  </form>
                </LeadRowCardSection>
                {couldNotLoadClientFromUrl && (
                  <LeadRowCard
                    className="text-xs bg-red-2 border-red-3 mt-3"
                    type="dashed"
                  >
                    <div className="flex flex-1 items-center justify-between gap-3">
                      <FontAwesomeIcon
                        width={14}
                        height={14}
                        icon={faFaceFrown}
                        className="w-4 h-4 shrink-0"
                      />
                      <p>
                        Something went wrong when fetching company information.
                        Please make sure that the url is valid and try again.
                      </p>
                    </div>
                  </LeadRowCard>
                )}
                <LeadRowCard
                  className="text-xs bg-red-1 border-red-2 mt-3"
                  type="dashed"
                >
                  <div className="flex flex-1 items-center gap-3">
                    <FontAwesomeIcon
                      width={14}
                      height={14}
                      icon={faMagicWandSparkles}
                      className="w-4 h-4 shrink-0"
                    />
                    <p>Add company domain to fetch info</p>
                  </div>
                </LeadRowCard>
              </>
            )}
          </LeadRowCard>
          {showMatrix && <LeadRowCard type="solid">
            <LeadRowCardSection title="Competence matrix">
              {hasMatrixTemplate ? (
                <div className="flex items-center justify-between px-1">
                  <span className="text-sm text-gray-600">File uploaded</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        if (lead?.id) homeState.matrixDrawerLeadId = lead.id;
                      }}
                      className="text-sm text-red-500 hover:underline"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        deleteMatrix.mutate(undefined, {
                          onSuccess: () => toast.success("Matrix cleared"),
                          onError: () => toast.error("Failed to clear matrix"),
                        })
                      }
                      disabled={deleteMatrix.isPending}
                      className="text-sm text-gray-400 hover:text-red-500 hover:underline disabled:opacity-50"
                    >
                      {deleteMatrix.isPending ? "Clearing…" : "Clear"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    ref={matrixFileInputRef}
                    type="file"
                    accept=".docx"
                    className="hidden"
                    onChange={handleMatrixUpload}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => matrixFileInputRef.current?.click()}
                    disabled={uploadMatrix.isPending}
                  >
                    {uploadMatrix.isPending ? "Uploading..." : "Upload .docx"}
                  </Button>
                </>
              )}
            </LeadRowCardSection>
          </LeadRowCard>}
          <LeadRowCard>
            <LeadRowCardSection title="Notes">
              <Input
                disabled={disabled}
                leftIcon={faGlobe}
                label="Whatever information you would like..."
                rows={3}
                onChange={(e) => setLocalNotes(e.target.value)}
                value={localNotes}
                onBlur={(e) => {
                  if (!(!lead && localNotes === "")) {
                    onMutateLead({ notes: localNotes });
                  }
                }}
              />
            </LeadRowCardSection>
          </LeadRowCard>
        </div>
        <LeadRowCard type="solid" className={stacked ? "row-start-1 min-w-0" : undefined}>
          {lead?.parsedAssignmentData &&
          lead?.parsedAssignmentData?.skills &&
          !couldNotLoadFromUrl &&
          !isEditingAssignmentLink ? (
            <>
              <LeadRowCardSectionWithLink
                title="Add assignment"
                linkUrl={""}
                onEditClick={() => onEditAssigmentLinkClick()}
              ></LeadRowCardSectionWithLink>
              {!isEditingAssignmentLink && (
                <LeadRowCardSection title="Role" secondarySection>
                  <Input
                    leftIcon={faBriefcase}
                    label="Role"
                    transformValue={trim}
                    onChange={(e) => setLocalAssignmentTitle(e.target.value)}
                    value={localAssignmentTitle}
                    onBlur={(e) => {
                      if (!(!lead && localAssignmentTitle === "")) {
                        if (lead.parsedAssignmentData) {
                          onMutateLead({
                            title: localAssignmentTitle,
                            parsedAssignmentData: {
                              ...lead.parsedAssignmentData!,
                              title: localAssignmentTitle,
                            },
                          });
                        }
                      }
                    }}
                  />
                </LeadRowCardSection>
              )}
              {lead.parsedAssignmentData.seniorityLevel !== "" && (
                <LeadRowCardSection
                  title="Seniority"
                  secondarySection
                  className="text-sm"
                >
                  <List>
                    <li>{lead.parsedAssignmentData.seniorityLevel}</li>
                    {lead.parsedAssignmentData.yearsOfExperience !== "" && (
                      <li>
                        {lead.parsedAssignmentData.yearsOfExperience} years of
                        experience
                      </li>
                    )}
                  </List>
                </LeadRowCardSection>
              )}
              {requiredSkills().length > 0 && (
                <LeadRowCardSection
                  title="Required technical skills"
                  secondarySection
                >
                  <div className="flex flex-row flex-wrap gap-2 px-4">
                    {requiredSkills().map((skill, index) => (
                      <EditorSkillTagButton
                        key={index}
                        text={skill}
                        handleClick={() => {}}
                        displayInfoOnHover
                      />
                    ))}
                  </div>
                </LeadRowCardSection>
              )}
              {lead.parsedAssignmentData.skills.technical_optional &&
                lead.parsedAssignmentData.skills.technical_optional.length >
                  0 && (
                  <LeadRowCardSection
                    title="Optional technical skills"
                    secondarySection
                  >
                    <div className="flex flex-row flex-wrap gap-2 px-4">
                      {lead.parsedAssignmentData.skills.technical_optional.map(
                        (skill, index) => (
                          <EditorSkillTagButton
                            key={index}
                            text={skill}
                            handleClick={() => {}}
                            displayInfoOnHover
                          />
                        ),
                      )}
                    </div>
                  </LeadRowCardSection>
                )}
              {skillMatchData && (
                <LeadRowCard
                  className="flex flex-1 flex-col bg-red-1 text-sm mt-2"
                  type="dashed"
                >
                  <p>{`${skillMatchData.length < 6 ? skillMatchData.length : 5} profiles have matching skills with this assignment`}</p>
                  <div className="flex flex-1 flex-row gap-3 overflow-y-scroll">
                    {skillMatchData.slice(0, 5).map((profileSkillMatch) => (
                      <Image
                        key={profileSkillMatch.profileId}
                        src={
                          getImageUrl(
                            getProfilePhotoIds(profileSkillMatch.profileId!)!,
                          )!
                        }
                        className="rounded-xl w-10 h-10 flex-shrink-0"
                        alt="Profile photo"
                      />
                    ))}
                  </div>
                </LeadRowCard>
              )}
              {lead.parsedAssignmentData.skills.soft_skills &&
                lead.parsedAssignmentData.skills.soft_skills.length > 0 && (
                  <LeadRowCardSection title="Soft skills" secondarySection>
                    <List>
                      {lead.parsedAssignmentData.skills.soft_skills.map(
                        (skill) => (
                          <li key={skill}>{skill}</li>
                        ),
                      )}
                    </List>
                  </LeadRowCardSection>
                )}
              {lead.parsedAssignmentData.skills.domain_expertise &&
                lead.parsedAssignmentData.skills.domain_expertise.length >
                  0 && (
                  <LeadRowCardSection title="Domain expertise" secondarySection>
                    <List>
                      {lead.parsedAssignmentData.skills.domain_expertise.map(
                        (skill) => (
                          <li key={skill}>{skill}</li>
                        ),
                      )}
                    </List>
                  </LeadRowCardSection>
                )}
              {lead.parsedAssignmentData.workplace !== "" && (
                <LeadRowCardSection
                  title="Workplace"
                  secondarySection
                  className="text-sm"
                >
                  <List>
                    <li>{lead.parsedAssignmentData.workplace}</li>
                    <li>{lead.parsedAssignmentData.workplaceInfo}</li>
                  </List>
                </LeadRowCardSection>
              )}
              {lead.parsedAssignmentData.sectorSpace !== "" && (
                <LeadRowCardSection title="Sector" secondarySection>
                  <List>
                    <li>
                      {lead.parsedAssignmentData.sectorSpace
                        .charAt(0)
                        .toUpperCase() +
                        lead.parsedAssignmentData.sectorSpace.slice(1)}
                    </li>
                  </List>
                </LeadRowCardSection>
              )}
              {lead.parsedAssignmentData.industry !== "" && (
                <LeadRowCardSection title="Industry" secondarySection>
                  <List>
                    <li>{lead.parsedAssignmentData.industry}</li>
                  </List>
                </LeadRowCardSection>
              )}
              {lead.parsedAssignmentData.profile !== "" && (
                <LeadRowCardSection
                  title="Profile"
                  secondarySection
                  className="text-sm"
                >
                  <List>
                    <li>{lead.parsedAssignmentData.profile}</li>
                  </List>
                </LeadRowCardSection>
              )}
              {lead.parsedAssignmentData.businessContext !== "" && (
                <LeadRowCardSection
                  title="Business context"
                  secondarySection
                  className="text-sm"
                >
                  <List>
                    <li>{lead.parsedAssignmentData.businessContext}</li>
                  </List>
                </LeadRowCardSection>
              )}

              {lead.parsedAssignmentData.responsibilities &&
                lead.parsedAssignmentData.responsibilities.length > 0 && (
                  <LeadRowCardSection title="Responsibilities" secondarySection>
                    <List>
                      {lead.parsedAssignmentData.responsibilities.map(
                        (responsibility) => (
                          <li key={responsibility}>{responsibility}</li>
                        ),
                      )}
                    </List>
                  </LeadRowCardSection>
                )}
              {lead.parsedAssignmentData.keySuccessFactors &&
                lead.parsedAssignmentData.keySuccessFactors.length > 0 && (
                  <LeadRowCardSection
                    title="Key success factors"
                    secondarySection
                  >
                    <List>
                      {lead.parsedAssignmentData.keySuccessFactors.map(
                        (factor) => (
                          <li key={factor}>{factor}</li>
                        ),
                      )}
                    </List>
                  </LeadRowCardSection>
                )}
              {lead.parsedAssignmentData.mustHaveQualifiers &&
                lead.parsedAssignmentData.mustHaveQualifiers.length > 0 && (
                  <LeadRowCardSection title="Must have" secondarySection>
                    <List>
                      {lead.parsedAssignmentData.mustHaveQualifiers.map(
                        (qualifier) => (
                          <li key={qualifier}>{qualifier}</li>
                        ),
                      )}
                    </List>
                  </LeadRowCardSection>
                )}
              {lead.parsedAssignmentData.niceToHaveQualifiers &&
                lead.parsedAssignmentData.niceToHaveQualifiers.length > 0 && (
                  <LeadRowCardSection title="Nice to have" secondarySection>
                    <List>
                      {lead.parsedAssignmentData.niceToHaveQualifiers.map(
                        (qualifier) => (
                          <li key={qualifier}>{qualifier}</li>
                        ),
                      )}
                    </List>
                  </LeadRowCardSection>
                )}
              {lead.parsedAssignmentData.location &&
                lead.parsedAssignmentData.location.length > 0 && (
                  <LeadRowCardSection title="Location" secondarySection>
                    <List>
                      {lead.parsedAssignmentData.location.map((location) => (
                        <li key={location}>{location}</li>
                      ))}
                    </List>
                  </LeadRowCardSection>
                )}
              {lead.parsedAssignmentData.remote !== "" && (
                <LeadRowCardSection title="Remote" secondarySection>
                  <List>
                    <li>{formatRemote(lead.parsedAssignmentData.remote)}</li>
                  </List>
                </LeadRowCardSection>
              )}
              {lead.parsedAssignmentData.date !== "" && (
                <LeadRowCardSection
                  title="Date"
                  secondarySection
                  className="text-sm"
                >
                  <List>
                    <li>{lead.parsedAssignmentData.date}</li>
                  </List>
                </LeadRowCardSection>
              )}
            </>
          ) : (
            <LeadRowCardSection title="Assignment">
              {!isEditingAssignmentLink && (
                <Input
                  disabled={disabled}
                  leftIcon={faBriefcase}
                  label="Role"
                  transformValue={trim}
                  onChange={(e) => setLocalAssignmentTitle(e.target.value)}
                  value={localAssignmentTitle}
                  onBlur={(e) => {
                    if (lead?.parsedAssignmentData) {
                      onMutateLead({
                        parsedAssignmentData: {
                          ...lead.parsedAssignmentData,
                          title: localAssignmentTitle,
                        },
                      });
                    } else if (localAssignmentTitle.trim() !== "") {
                      //todo: create new parsed assignent info
                    }
                  }}
                  className="mb-4"
                />
              )}
              <Input
                disabled={disabled}
                leftIcon={faGlobe}
                label="Link"
                validate={isUrlOrEmpty}
                value={lead.parsedAssignmentData.sourceUrl || ""}
                rightButton={{
                  label: "Go!",
                  onClick: onRequestAssignmentInfo,
                }}
              />
              <SecondarySectionTitle className="text-sm">
                OR
              </SecondarySectionTitle>
              <Input
                disabled={disabled}
                leftIcon={faGlobe}
                label="Paste assignment text"
                validate={isString}
                value={lead.parsedAssignmentData.sourceText || ""}
                rows={5}
                rightButton={{
                  label: "Go!",
                  onClick: onRequestAssignmentInfoWithInfo,
                }}
              />
              {couldNotLoadFromUrl ? (
                <LeadRowCard
                  className="text-xs bg-red-2 border-red-3 mt-3"
                  type="dashed"
                >
                  <div className="flex flex-1 items-center justify-between gap-3">
                    <FontAwesomeIcon
                      width={14}
                      height={14}
                      icon={faFaceFrown}
                      className="w-4 h-4 shrink-0"
                    />
                    <p>
                      Assignment info could not be fetched from the link. Please
                      try to paste the assignment description instead.
                    </p>
                  </div>
                </LeadRowCard>
              ) : (
                <LeadRowCard
                  className="text-xs bg-red-1 border-red-2 mt-3"
                  type="dashed"
                >
                  <div className="flex flex-1 items-center justify-between gap-3">
                    <FontAwesomeIcon
                      width={14}
                      height={14}
                      icon={faMagicWandSparkles}
                      className="w-4 h-4 shrink-0"
                    />
                    <p>
                      By adding an assignment link, VOCS will help you find a
                      matching candidate and help you write suiting resume
                      introductions.
                    </p>
                  </div>
                </LeadRowCard>
              )}
            </LeadRowCardSection>
          )}
        </LeadRowCard>
      </PanelContainer>
    );
  }

  return (
    <PanelContainer className={cn(stacked && "grid-cols-1", className)}>
      <div className={cn("flex flex-col gap-6 lg:justify-start", stacked && "row-start-2 min-w-0")}>
        <LeadRowCard type="solid">
          <LeadRowCardSection title="Contact information">
            <Input
              leftIcon={faPerson}
              label="Contact name"
              value={localContactName}
              onChange={(e) => setLocalContactName(e.target.value)}
              onBlur={(e) => {
                if (!(!lead && localContactName === "")) {
                  onMutateLead({ contactPersonName: localContactName });
                }
              }}
              transformValue={trim}
            />
            <Input
              leftIcon={faAt}
              label="E-mail"
              value={lead?.contactPersonEmail}
              onChange={(e) => setLocalContactEmail(e.target.value)}
              onBlur={(e) => {
                if (!(!lead && localContactEmail === "")) {
                  onMutateLead({ contactPersonEmail: localContactEmail });
                }
              }}
              transformValue={trim}
              validate={isEmailOrEmpty}
              type="email"
            />
            <Input
              leftIcon={faPhone}
              label="Phone number"
              value={lead?.contactPersonPhone}
              onChange={(e) => setLocalContactPhone(e.target.value)}
              onBlur={(e) => {
                if (!(!lead && localContactPhone === "")) {
                  onMutateLead({ contactPersonPhone: localContactPhone });
                }
              }}
              transformValue={trim}
              validate={isPhoneNumberOrEmpty}
              type="tel"
            />
          </LeadRowCardSection>
        </LeadRowCard>
        <LeadRowCard type="solid">
          {lead?.companyUrl && !isEditingWebsiteLink ? (
            <>
              <LeadRowCardSectionWithLink
                title="Company"
                linkUrl={lead.companyUrl}
                onEditClick={() => onEditWebsiteLinkClick()}
              ></LeadRowCardSectionWithLink>
              <LeadRowCardSection title="Name" secondarySection>
                <Input
                  disabled={disabled}
                  leftIcon={faGlobe}
                  label="Company website link"
                  onChange={(e) => setLocalCompanyName(e.target.value)}
                  value={localCompanyName}
                  onBlur={(e) => {
                    if (!(!lead && localCompanyName === "")) {
                      onMutateLead({ companyName: localCompanyName });
                    }
                  }}
                />
              </LeadRowCardSection>
              <LeadRowCardSection title="Pick image">
                <div className="flex flex-row flex-wrap gap-2">
                  {lead?.logoSuggestions &&
                    lead?.logoSuggestions.map((logo, i) => (
                      <ImageButton
                        key={logo}
                        active={lead.logoSuggestionOrigin === logo}
                        alt={`Logo suggestion ${i}`}
                        imageUrl={logo}
                        onClick={() => onSelectLogo(logo)}
                      />
                    ))}
                  <TextButton
                    key={"custom-icon"}
                    active={
                      !lead.logoSuggestionOrigin ||
                      lead.logoSuggestionOrigin === ""
                    }
                    onClick={() => {
                      onSelectLogo("");
                    }}
                    text={lead.companyName ? lead.companyName[0] : ""}
                  />
                </div>
              </LeadRowCardSection>
              <LeadRowCardSection
                title="Description"
                secondarySection
                className="text-sm"
              >
                {lead.companyDesc}
              </LeadRowCardSection>
            </>
          ) : (
            <>
              <LeadRowCardSection title="Company">
                <form onSubmit={handleCompanyUrlSubmit}>
                  <Input
                    ref={companyUrlInputRef}
                    disabled={disabled}
                    leftIcon={faGlobe}
                    label="Company website link"
                    validate={isUrlWithMaxLengthOrEmpty}
                    value={lead?.companyUrl}
                    rightButton={{
                      label: "Go!",
                      onClick: () => {
                        if (
                          companyUrlInputRef.current?.value &&
                          companyUrlInputRef.current?.value.length < 200
                        )
                          onRequestWebsiteInfo(
                            companyUrlInputRef.current?.value || "",
                          );
                      },
                    }}
                    // Add onKeyDown handler if you specifically want to handle Enter key
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onRequestWebsiteInfo(
                          companyUrlInputRef.current?.value || "",
                        );
                      }
                    }}
                  />
                </form>
              </LeadRowCardSection>
              {couldNotLoadClientFromUrl && (
                <LeadRowCard
                  className="text-xs bg-red-2 border-red-3 mt-3"
                  type="dashed"
                >
                  <div className="flex flex-1 items-center justify-between gap-3">
                    <FontAwesomeIcon
                      width={14}
                      height={14}
                      icon={faFaceFrown}
                      className="w-4 h-4 shrink-0"
                    />
                    <p>
                      Something went wrong when fetching company information.
                      Please make sure that the url is valid and try again.
                    </p>
                  </div>
                </LeadRowCard>
              )}
              <LeadRowCard
                className="text-xs bg-red-1 border-red-2 mt-3"
                type="dashed"
              >
                <div className="flex flex-1 items-center gap-3">
                  <FontAwesomeIcon
                    width={14}
                    height={14}
                    icon={faMagicWandSparkles}
                    className="w-4 h-4 shrink-0"
                  />
                  <p>Add company domain to fetch info</p>
                </div>
              </LeadRowCard>
            </>
          )}
        </LeadRowCard>
        <LeadRowCard>
          <LeadRowCardSection title="Notes">
            <Input
              disabled={disabled}
              leftIcon={faGlobe}
              label="Whatever information you would like..."
              rows={3}
              onChange={(e) => setLocalNotes(e.target.value)}
              value={localNotes}
              onBlur={(e) => {
                if (!(!lead && localNotes === "")) {
                  onMutateLead({ notes: localNotes });
                }
              }}
            />
          </LeadRowCardSection>
        </LeadRowCard>
      </div>
      <LeadRowCard type="solid" className={stacked ? "row-start-1 min-w-0" : undefined}>
        {lead?.assignmentInfo &&
        lead?.assignmentInfo?.skills &&
        lead?.assignmentInfo?.skills.length > 0 &&
        !couldNotLoadFromUrl &&
        !isEditingAssignmentLink ? (
          <>
            <LeadRowCardSectionWithLink
              title="Add assignment"
              linkUrl={lead.assignmentInfo.assignmentUrl}
              onEditClick={() => onEditAssigmentLinkClick()}
            ></LeadRowCardSectionWithLink>
            {!isEditingAssignmentLink && (
              <LeadRowCardSection title="Role" secondarySection>
                <Input
                  leftIcon={faBriefcase}
                  label="Role"
                  transformValue={trim}
                  onChange={(e) => setLocalAssignmentTitle(e.target.value)}
                  value={localAssignmentTitle}
                  onBlur={(e) => {
                    if (!(!lead && localAssignmentTitle === "")) {
                      if (lead.assignmentInfo) {
                        onMutateLead({
                          title: localAssignmentTitle,
                          assignmentInfo: {
                            ...lead.assignmentInfo!,
                            title: localAssignmentTitle,
                          },
                        });
                      }
                    }
                  }}
                />
              </LeadRowCardSection>
            )}
            {lead?.assignmentInfo?.skillTags &&
              lead.assignmentInfo.skillTags.length > 0 && (
                <LeadRowCardSection title="Technical Skills" secondarySection>
                  <div className="flex flex-row flex-wrap gap-2 px-4">
                    {lead.assignmentInfo.skillTags.map((skill, index) => (
                      <TooltipProvider key={index} delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-6 h-6 flex items-center justify-center">
                              <SkillsIcon
                                key={index}
                                profileSkillTag={skill}
                                text=""
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="mb-2">
                            <p>{skill?.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                  </div>
                  {skillMatchData && (
                    <LeadRowCard
                      className="flex flex-1 flex-col bg-red-1 text-sm mt-2"
                      type="dashed"
                    >
                      <p>{`${skillMatchData.length < 6 ? skillMatchData.length : 5} profiles have matching skills with this assignment`}</p>
                      <div className="flex flex-1 flex-row gap-3 overflow-y-scroll">
                        {skillMatchData.slice(0, 5).map((profileSkillMatch) => (
                          <Image
                            key={profileSkillMatch.profileId}
                            src={
                              getImageUrl(
                                getProfilePhotoIds(
                                  profileSkillMatch.profileId!,
                                )!,
                              )!
                            }
                            className="rounded-xl w-10 h-10 flex-shrink-0"
                            alt="Profile photo"
                          />
                        ))}
                      </div>
                    </LeadRowCard>
                  )}
                </LeadRowCardSection>
              )}
            {lead.assignmentInfo.skills &&
              lead.assignmentInfo.skills.length > 0 && (
                <LeadRowCardSection title="All skills" secondarySection>
                  <List ref={skillsParent}>
                    {lead.assignmentInfo.skills
                      .slice(0, expanded ? undefined : MAX_VISIBLE_SKILLS)
                      .map((skill) => (
                        <li key={skill}>{skill}</li>
                      ))}
                    {lead.assignmentInfo.skills.length > MAX_VISIBLE_SKILLS && (
                      <Button className="mt-2 w-max" onClick={toggleExpanded}>
                        {expanded ? "show less" : "show more"}
                      </Button>
                    )}
                  </List>
                </LeadRowCardSection>
              )}
            {lead.assignmentInfo.workplace !== "" && (
              <LeadRowCardSection
                title="Workplace"
                secondarySection
                className="text-sm"
              >
                <List>
                  <li>{lead.assignmentInfo.workplace}</li>
                  <li>{lead.assignmentInfo.workplaceInfo}</li>
                </List>
              </LeadRowCardSection>
            )}
            {lead.assignmentInfo.sector && (
              <LeadRowCardSection title="Industry" secondarySection>
                <List>
                  <li>{lead.assignmentInfo.sector}</li>
                </List>
              </LeadRowCardSection>
            )}
            {lead.assignmentInfo.profile !== "" && (
              <LeadRowCardSection
                title="Profile"
                secondarySection
                className="text-sm"
              >
                <List>
                  <li>{lead.assignmentInfo.profile}</li>
                </List>
              </LeadRowCardSection>
            )}

            {lead.assignmentInfo.responsibilities &&
              lead.assignmentInfo.responsibilities.length > 0 && (
                <LeadRowCardSection title="Responsibilities" secondarySection>
                  <List>
                    {lead.assignmentInfo.responsibilities.map(
                      (responsibility) => (
                        <li key={responsibility}>{responsibility}</li>
                      ),
                    )}
                  </List>
                </LeadRowCardSection>
              )}
            {lead.assignmentInfo.location &&
              lead.assignmentInfo.location.length > 0 && (
                <LeadRowCardSection title="Location" secondarySection>
                  <List>
                    {lead.assignmentInfo.location.map((location) => (
                      <li key={location}>{location}</li>
                    ))}
                  </List>
                </LeadRowCardSection>
              )}
            {lead.assignmentInfo.remote &&
              lead.assignmentInfo.remote !== "" && (
                <LeadRowCardSection title="Remote" secondarySection>
                  <List>
                    <li>{formatRemote(lead.assignmentInfo.remote)}</li>
                  </List>
                </LeadRowCardSection>
              )}
          </>
        ) : (
          <LeadRowCardSection title="Assignment">
            {!isEditingAssignmentLink && (
              <Input
                disabled={disabled}
                leftIcon={faBriefcase}
                label="Role"
                transformValue={trim}
                onChange={(e) => setLocalAssignmentTitle(e.target.value)}
                value={localAssignmentTitle}
                onBlur={(e) => {
                  if (lead?.assignmentInfo) {
                    onMutateLead({
                      assignmentInfo: {
                        ...lead.assignmentInfo,
                        title: localAssignmentTitle,
                      },
                    });
                  } else if (localAssignmentTitle.trim() !== "") {
                    onMutateLead({
                      assignmentInfo: {
                        title: localAssignmentTitle,
                        skills: [],
                        allSkills: [],
                        responsibilities: [],
                        location: [],
                        remote: "",
                        workplace: "",
                        workplaceInfo: "",
                        profile: "",
                        assignmentUrl: "",
                      },
                    });
                  }
                }}
                className="mb-4"
              />
            )}
            <Input
              disabled={disabled}
              leftIcon={faGlobe}
              label="Link"
              validate={isUrlOrEmpty}
              value={lead?.assignmentInfo?.assignmentUrl}
              rightButton={{
                label: "Go!",
                onClick: onRequestAssignmentInfo,
              }}
            />
            <SecondarySectionTitle className="text-sm">
              OR
            </SecondarySectionTitle>
            <Input
              disabled={disabled}
              leftIcon={faGlobe}
              label="Paste assignment text"
              validate={isString}
              value={lead?.assignmentInfo?.assignmentDescription}
              rows={5}
              rightButton={{
                label: "Go!",
                onClick: onRequestAssignmentInfoWithInfo,
              }}
            />
            {couldNotLoadFromUrl ? (
              <LeadRowCard
                className="text-xs bg-red-2 border-red-3 mt-3"
                type="dashed"
              >
                <div className="flex flex-1 items-center justify-between gap-3">
                  <FontAwesomeIcon
                    width={14}
                    height={14}
                    icon={faFaceFrown}
                    className="w-4 h-4 shrink-0"
                  />
                  <p>
                    Assignment info could not be fetched from the link. Please
                    try to paste the assignment description instead.
                  </p>
                </div>
              </LeadRowCard>
            ) : (
              <LeadRowCard
                className="text-xs bg-red-1 border-red-2 mt-3"
                type="dashed"
              >
                <div className="flex flex-1 items-center justify-between gap-3">
                  <FontAwesomeIcon
                    width={14}
                    height={14}
                    icon={faMagicWandSparkles}
                    className="w-4 h-4 shrink-0"
                  />
                  <p>
                    By adding an assignment link, VOCS will help you find a
                    matching candidate and help you write suiting resume
                    introductions.
                  </p>
                </div>
              </LeadRowCard>
            )}
          </LeadRowCardSection>
        )}
      </LeadRowCard>
    </PanelContainer>
  );
};

const List = forwardRef<HTMLUListElement, PropsWithChildren>(
  ({ children }, ref) => (
    <ul ref={ref} className="list-disc list-inside text-sm px-4">
      {children}
    </ul>
  ),
);

List.displayName = "List";

AssignmentPanel.displayName = "AssignmentPanel";
